import { GanttChart, GitBranch } from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { useProjectTasks } from '@/hooks/use-projects';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { formatDateOnly, formatLocalDate } from '@/lib/date-utils';

export function GanttTab({ projectId }: { projectId: number }) {
  const { data: tasks } = useProjectTasks({ project: projectId, page_size: 200 });

  const allTasks = tasks?.results ?? [];
  const tasksWithDates = allTasks.filter((t) => t.start_date && t.end_date);

  if (tasksWithDates.length === 0) {
    return (
      <div className="text-center py-12">
        <GanttChart className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">No tasks with dates to display.</p>
        <p className="text-sm text-gray-500 mt-1">
          Add start and end dates to tasks to see the Gantt chart.
        </p>
      </div>
    );
  }

  // Calculate date range
  const dates = tasksWithDates.flatMap((t) => [
    parseISO(t.start_date!),
    parseISO(t.end_date!),
  ]);
  const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
  const totalDays = differenceInCalendarDays(maxDate, minDate) + 1;

  const getBarStyle = (task: { start_date?: string | null; end_date?: string | null }) => {
    if (!task.start_date || !task.end_date) return {};
    const start = parseISO(task.start_date);
    const end = parseISO(task.end_date);
    const left = (differenceInCalendarDays(start, minDate) / totalDays) * 100;
    const width = (differenceInCalendarDays(end, start) / totalDays) * 100;
    return { left: `${left}%`, width: `${Math.max(width, 1)}%` };
  };

  const getBarColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'blocked': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  // Generate month labels
  const monthLabels: { label: string; left: string }[] = [];
  const cursor = new Date(minDate);
  cursor.setDate(1);
  while (cursor <= maxDate) {
    const dayOffset = Math.max(0, differenceInCalendarDays(cursor, minDate));
    monthLabels.push({
      label: formatDateOnly(formatLocalDate(cursor), '—', { month: 'short', year: '2-digit' }),
      left: `${(dayOffset / totalDays) * 100}%`,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Project Timeline</h3>
      <Card>
        <CardContent className="p-4 overflow-x-auto">
          {/* Month headers */}
          <div className="relative h-6 mb-2 border-b" style={{ minWidth: '600px' }}>
            {monthLabels.map((m, i) => (
              <span
                key={i}
                className="absolute text-xs text-gray-500 font-medium"
                style={{ left: m.left }}
              >
                {m.label}
              </span>
            ))}
          </div>

          {/* Task rows */}
          <div className="space-y-2" style={{ minWidth: '600px' }}>
            {tasksWithDates.map((task) => (
              <div key={task.id} className="flex items-center gap-2 h-8">
                <div className="w-40 flex-shrink-0 truncate text-sm text-gray-700" title={task.title}>
                  {task.title}
                </div>
                <div className="flex-1 relative h-6 bg-gray-100 rounded">
                  <div
                    className={`absolute h-6 rounded ${getBarColor(task.status)} opacity-80`}
                    style={getBarStyle(task)}
                    title={`${task.title}: ${formatDateOnly(task.start_date)} - ${formatDateOnly(task.end_date)}`}
                  />
                  {task.depends_on && (
                    <div className="absolute -left-3 top-1/2 -translate-y-1/2">
                      <GitBranch className="h-3 w-3 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex gap-4 mt-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-400 rounded" />
              <span>To Do</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              <span>In Progress</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded" />
              <span>Completed</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded" />
              <span>Blocked</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
